import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fbqTrack } from '../lib/fbpixel';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ComplianceLinks, EduDisclaimer } from '../components/PublicShell';
import { TrendingUp, Loader2, Eye, EyeOff, CheckCircle, GraduationCap } from 'lucide-react';
import { MOTTO, getGeoCountry } from '../config/brand';

export default function Register() {
    const { t, i18n } = useTranslation();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password !== confirmPassword) return setError(t('auth.register.errors.passwordMismatch'));
        if (password.length < 6) return setError(t('auth.register.errors.passwordTooShort'));
        setLoading(true);
        const extra = { lang: (i18n.language || 'en').split('-')[0] };
        const cc = getGeoCountry();
        if (cc) extra.country = cc;
        const result = await register(name, email, password, 'exness', extra);
        if (result.success) {
            fbqTrack('CompleteRegistration', { content_name: 'Free Student Account', content_category: 'education' });
            navigate('/dashboard', { replace: true });
        } else {
            setError(result.error);
        }
        setLoading(false);
    };

    const perks = [
        t('auth.register.free.perk1', 'Student dashboard & full curriculum map'),
        t('auth.register.free.perk2', 'Free lessons, study group and weekly chart reviews'),
        t('auth.register.free.perk3', 'Unlock full training modules & practice tools from your dashboard'),
    ];

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background-base)' }} data-testid="register-page">
            <div className="absolute top-4 end-4 z-10"><LanguageSwitcher /></div>
            <div className="flex-1 flex items-center justify-center px-4 py-14">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center gap-2 mb-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--primary-color)' }}>
                                <TrendingUp className="w-5 h-5" style={{ color: 'var(--primary-text)' }} />
                            </div>
                            <span className="text-xl font-black" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>NIKAYO FX GUIDE</span>
                        </Link>
                        <p className="text-[10px] italic font-semibold tracking-[0.14em]" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--primary-color)' }} data-testid="brand-motto">“{MOTTO}”</p>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-black tracking-widest uppercase mt-5" style={{ backgroundColor: 'var(--primary-muted)', color: 'var(--primary-color)', border: '1px solid var(--border-highlight)' }} data-testid="register-free-badge">
                            <GraduationCap className="w-3.5 h-3.5" /> {t('auth.register.free.badge', 'Free registration')}
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mt-3" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>{t('auth.register.free.title', 'Create your free student account')}</h1>
                        <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>{t('auth.register.free.subtitle', 'No payment and no deposit needed to register. Learn at your own pace.')}</p>
                    </div>

                    <ul className="space-y-2 mb-6 rounded-2xl p-4" style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' }} data-testid="register-perks">
                        {perks.map((p) => (
                            <li key={p} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}><CheckCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--primary-color)' }} />{p}</li>
                        ))}
                    </ul>

                    <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' }} data-testid="register-form-container">
                        {error && <div className="mb-4 rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'rgba(220,38,38,0.08)', color: '#FCA5A5', border: '1px solid rgba(220,38,38,0.3)' }} data-testid="register-error">{error}</div>}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="name" style={{ color: 'var(--text-secondary)' }}>{t('auth.register.name')}</Label>
                                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" required autoComplete="name" className="input-field w-full" data-testid="register-name-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="email" style={{ color: 'var(--text-secondary)' }}>{t('auth.register.email')}</Label>
                                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" className="input-field w-full ltr-only" data-testid="register-email-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="password" style={{ color: 'var(--text-secondary)' }}>{t('auth.register.password')}</Label>
                                <div className="relative">
                                    <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} autoComplete="new-password" className="input-field w-full pr-10 ltr-only" data-testid="register-password-input" />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} data-testid="toggle-password-btn">
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="confirmPassword" style={{ color: 'var(--text-secondary)' }}>{t('auth.register.confirmPassword')}</Label>
                                <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" required autoComplete="new-password" className="input-field w-full ltr-only" data-testid="register-confirm-password-input" />
                            </div>
                            <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit-btn">
                                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('auth.register.submitting')}</> : t('auth.register.free.submit', 'Create Free Account')}
                            </Button>
                        </form>
                        <p className="mt-5 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {t('auth.register.haveAccount')}{' '}
                            <Link to="/login" className="font-medium hover:underline" style={{ color: 'var(--primary-color)' }} data-testid="login-link">{t('auth.register.signInLink')}</Link>
                        </p>
                    </div>
                    <div className="mt-8 space-y-3">
                        <ComplianceLinks dark={false} />
                        <EduDisclaimer dark={false} />
                    </div>
                </div>
            </div>
        </div>
    );
}
